import type { VideoPlayer } from '@zoom/videosdk';
import { TFunction } from 'i18next';
import { CSSProperties, ReactNode, RefObject } from 'react';

import { AvatarAccent, InitialsAvatar, Text, cn } from '@growtherapy/sprout-ui';

import { ActiveSpeakerOutline } from '../../components/ActiveSpeakerOutline';
import { nonEssentialElementProps } from '../../hooks/nonEssentialElementProps';
import { UserType } from '../../types';
import { nameToInitials } from '../../utils';
import { useRemoteVideoComponent } from '../../video-sdk/actions/useComponentActions';
import { Participant } from '../../video-sdk/core/room/participant/Participant';
import { parseIdentity } from '../../video-sdk/core/room/participant/utils';
import { VideoPlatform } from '../../video-sdk/videoPlatform';
import { UseRemoteMediaProps } from '../useRemoteMedia';
import { RemoteParticipantReconnectingOverlayWrapperProps } from './reconnect/RemoteParticipantReconnectingOverlayWrapper';

export type NameBadgeWrapperProps = {
  className?: string;
  participant: Participant;
};

export type RemoteParticipantProps = {
  className?: string;
  /** Applied to the remote video element itself, e.g. to control `object-fit` */
  videoClassName?: string;
  /** Applied to the remote video element itself, e.g. to control `aspect-ratio` */
  videoStyle?: CSSProperties;
  isProviderView?: boolean;
  isVideoOff?: boolean;
  isOwnAudioOff?: boolean;
  isOwnVideoOff?: boolean;
  isScreenShareMode?: boolean;
  isSpeaking?: boolean;
  videoRef: RefObject<HTMLVideoElement | VideoPlayer>;
  audioRef: RefObject<HTMLAudioElement>;
  nameBadgeComponent: (props: NameBadgeWrapperProps) => ReactNode;
  remoteParticipantReconnectingOverlayComponent: (props: RemoteParticipantReconnectingOverlayWrapperProps) => ReactNode;
  remoteVideoComponent: ReturnType<typeof useRemoteVideoComponent>;
  isReconnecting?: boolean;
  t: TFunction;
  videoPlatform: VideoPlatform;
} & UseRemoteMediaProps;

export type RemoteParticipantWrapperProps = Pick<
  RemoteParticipantProps,
  'className' | 'videoClassName' | 'videoStyle'
> &
  UseRemoteMediaProps;

function getVideoOffMessaging(
  t: TFunction,
  isProviderView: boolean,
  remoteParticipantIsProvider: boolean,
  isOwnVideoOff?: boolean,
  isOwnAudioOff?: boolean,
) {
  const canSee = !isOwnVideoOff;
  const canHear = !isOwnAudioOff;

  if (isProviderView) {
    if (canSee && canHear) return t('telehealth-core:video.yourClientHasCameraOffButCanStillSeeAndHear');
    if (canSee) return t('telehealth-core:video.yourClientHasCameraOffButCanStillSee');
    if (canHear) return t('telehealth-core:video.yourClientHasCameraOffButCanStillHear');
    return t('telehealth-core:video.yourClientHasCameraOff');
  }

  if (remoteParticipantIsProvider) {
    if (canSee && canHear) return t('telehealth-core:video.yourProviderHasCameraOffButCanStillSeeAndHear');
    if (canSee) return t('telehealth-core:video.yourProviderHasCameraOffButCanStillSee');
    if (canHear) return t('telehealth-core:video.yourProviderHasCameraOffButCanStillHear');
    return t('telehealth-core:video.yourProviderHasCameraOff');
  }

  if (canSee && canHear) return t('telehealth-core:video.thisPersonHasCameraOffButCanStillSeeAndHear');
  if (canSee) return t('telehealth-core:video.thisPersonHasCameraOffButCanStillSee');
  if (canHear) return t('telehealth-core:video.thisPersonHasCameraOffButCanStillHear');
  return t('telehealth-core:video.thisPersonHasCameraOff');
}

export function RemoteParticipantCell({
  audioRef,
  className,
  isOwnAudioOff,
  isOwnVideoOff,
  isProviderView,
  isScreenShareMode,
  isSpeaking,
  isVideoOff,
  nameBadgeComponent: NameBadgeComponent,
  remoteParticipant,
  videoRef,
  isReconnecting,
  remoteParticipantReconnectingOverlayComponent: RemoteParticipantReconnectingOverlayComponent,
  remoteVideoComponent: RemoteVideoComponent,
  t,
  videoClassName,
  videoPlatform,
  videoStyle,
}: RemoteParticipantProps) {
  const { id, userType } = parseIdentity(remoteParticipant?.identity);
  const participantName = remoteParticipant?.displayName;

  const remoteParticipantIsProvider = userType === UserType.PROVIDER;
  const displayInitials = participantName ? nameToInitials(participantName) : '';

  const showVideoOffMessaging = !isScreenShareMode;
  const videoOffMessaging = getVideoOffMessaging(
    t,
    !!isProviderView,
    remoteParticipantIsProvider,
    isOwnVideoOff,
    isOwnAudioOff,
  );

  const videoPlatformIsZoom = videoPlatform === VideoPlatform.Zoom;

  return (
    <div
      className={cn(
        'relative overflow-hidden bg-neutral-900 rounded-2xl flex justify-center items-center',
        'rebrand:bg-surface-inverse',
        className,
      )}
      style={{
        // Clips reconnect overlay bg blur by rounded border
        clipPath: 'inset(0 round 16px)',
      }}
      data-testid={`remote-audio-video:${id}`}
      /* 
         For accessibility: The aria-label and aria-labelledby attributes are not well supported 
         on the HTML5 <audio> and <video> elements at this time. In the meantime, we can 
         add the label to the parent element.
        */
      role='region'
      aria-label={t('telehealth-core:video.liveRemoteParticipantVideoAndAudio')}
    >
      {isVideoOff && !isReconnecting && (
        <div className='absolute isolate flex flex-col items-center'>
          <InitialsAvatar
            initials={displayInitials}
            size={isScreenShareMode ? 'sm' : 'md'}
            className={cn('text-center flex items-center justify-center')}
            accent={AvatarAccent.Green}
          />
          {showVideoOffMessaging && (
            <Text
              variant={isScreenShareMode ? 'xs' : 'sm'}
              className='text-neutral-600 p-4 text-center rebrand:text-text-inverse'
              data-testid='video-off-messaging'
            >
              {videoOffMessaging}
            </Text>
          )}
        </div>
      )}

      <RemoteVideoComponent
        remoteParticipantId={id}
        audioRef={audioRef}
        videoRef={videoRef}
        videoClassName={videoClassName}
        videoStyle={videoStyle}
      />

      {isReconnecting && (
        <RemoteParticipantReconnectingOverlayComponent remoteParticipantIdentity={remoteParticipant.identity} />
      )}

      {participantName && (
        <NameBadgeComponent
          participant={remoteParticipant}
          className={cn(`absolute bottom-0 left-0 ${nonEssentialElementProps.className}`, {
            'z-[1]': videoPlatformIsZoom,
          })}
        />
      )}
      <ActiveSpeakerOutline isOutlined={!!isSpeaking} />
    </div>
  );
}
